import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Activity, TrendingUp, ShieldAlert, Scroll, DollarSign, Rocket, Send } from "lucide-react";
import { toast } from "sonner";

function StatTile({ label, value, sub, icon: Icon, tone = "primary" }) {
  const toneCls = tone === "danger" ? "text-rose-500" : tone === "warn" ? "text-amber-500" : "text-primary";
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg bg-muted flex items-center justify-center`}>
            <Icon className={`w-5 h-5 ${toneCls}`} />
          </div>
          <div>
            <div className="text-xs uppercase text-muted-foreground tracking-wide">{label}</div>
            <div className="text-2xl font-bold" data-testid={`stat-${label.toLowerCase().replace(/\s+/g,'-')}`}>{value}</div>
            {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminAnalytics() {
  const [summary, setSummary] = useState(null);
  const [mrr, setMrr] = useState([]);
  const [sla, setSla] = useState(null);
  const [audit, setAudit] = useState([]);
  const [fraud, setFraud] = useState({ rows: [], counts: {}, rules: [] });
  const [scanning, setScanning] = useState(false);
  const [broadcasting, setBroadcasting] = useState(false);
  const [testSending, setTestSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, m, sl, a, f] = await Promise.all([
        api.get("/admin/analytics/summary"),
        api.get("/admin/analytics/mrr"),
        api.get("/admin/analytics/sla"),
        api.get("/admin/analytics/audit-log?limit=100"),
        api.get("/admin/analytics/fraud-alerts"),
      ]);
      setSummary(s.data);
      setMrr(m.data);
      setSla(sl.data);
      setAudit(a.data);
      setFraud(f.data);
    } catch (e) {
      toast.error("Failed to load analytics");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const scan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/admin/analytics/fraud-scan");
      toast.success(`Scan complete — ${data.flagged} flagged of ${data.scanned}`);
      await load();
    } catch { toast.error("Scan failed"); }
    setScanning(false);
  };

  const sendLaunchEmail = async (testOnly) => {
    const setter = testOnly ? setTestSending : setBroadcasting;
    if (!testOnly && !window.confirm("Send Play Store launch email to ALL customers? This cannot be undone.")) return;
    setter(true);
    try {
      const { data } = await api.post("/marketing/launch-announcement", { test_only: testOnly });
      toast.success(`${testOnly ? "Test" : "Broadcast"} sent — ${data.sent} delivered, ${data.failed} failed`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    }
    setter(false);
  };

  const maxMrr = Math.max(1, ...mrr.map(m => m.mrr_gbp));

  return (
    <div className="space-y-6" data-testid="admin-analytics-page">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
          <Activity className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h1 className="text-3xl font-display font-bold">Ops analytics</h1>
          <p className="text-sm text-muted-foreground">MRR · Churn · SLA · Audit · Fraud rules</p>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatTile label="MRR" value={`£${summary.mrr_gbp.toLocaleString()}`} sub={`ARR £${summary.arr_gbp.toLocaleString()}`} icon={DollarSign} />
          <StatTile label="Active subs" value={summary.active_subscriptions} sub={`Churn ${summary.churn_pct}%`} icon={TrendingUp} />
          <StatTile label="SLA %" value={`${summary.sla_pct}%`} sub={`${summary.tickets_breached} breached`} icon={ShieldAlert} tone={summary.sla_pct < 90 ? "warn" : "primary"} />
          <StatTile label="Stuck claims" value={summary.stuck_claims} sub={`${summary.total_users} users`} icon={Activity} tone={summary.stuck_claims > 0 ? "warn" : "primary"} />
        </div>
      )}

      <Card className="border-primary/30 bg-primary/5" data-testid="launch-announcement-card">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row md:items-center gap-4 justify-between">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                <Rocket className="w-5 h-5" />
              </div>
              <div>
                <div className="font-display font-semibold text-lg">Play Store launch email</div>
                <p className="text-sm text-muted-foreground max-w-lg">
                  Broadcast the Google Play link to every customer via Resend. Test to your own inbox first, then send to all.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => sendLaunchEmail(true)}
                disabled={testSending || broadcasting}
                data-testid="launch-email-test-btn"
              >
                <Send className="w-3.5 h-3.5 mr-1.5" />
                {testSending ? "Sending…" : "Send test to me"}
              </Button>
              <Button
                size="sm"
                onClick={() => sendLaunchEmail(false)}
                disabled={testSending || broadcasting}
                className="bg-primary text-primary-foreground"
                data-testid="launch-email-broadcast-btn"
              >
                <Rocket className="w-3.5 h-3.5 mr-1.5" />
                {broadcasting ? "Broadcasting…" : "Send to all customers"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="mrr" className="w-full">
        <TabsList data-testid="analytics-tabs">
          <TabsTrigger value="mrr" data-testid="tab-mrr">MRR trend</TabsTrigger>
          <TabsTrigger value="sla" data-testid="tab-sla">SLA</TabsTrigger>
          <TabsTrigger value="fraud" data-testid="tab-fraud">Fraud alerts</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit log</TabsTrigger>
        </TabsList>

        <TabsContent value="mrr">
          <Card>
            <CardHeader><CardTitle>MRR trend (last 12 months)</CardTitle></CardHeader>
            <CardContent>
              <div className="flex items-end gap-2 h-48 border-b border-border pb-2">
                {mrr.map(m => (
                  <div key={m.period} className="flex-1 flex flex-col items-center gap-1" title={`${m.period}: £${m.mrr_gbp} · ${m.active} subs`}>
                    <div className="w-full bg-primary/70 rounded-t" style={{ height: `${(m.mrr_gbp / maxMrr) * 100}%` }} />
                    <div className="text-[9px] text-muted-foreground">{m.period.slice(5)}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sla">
          {sla && (
            <Card>
              <CardHeader><CardTitle>SLA breakdown</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(sla.by_priority).map(([pri, s]) => (
                    <div key={pri} className="p-3 rounded-lg bg-muted">
                      <div className="text-xs uppercase text-muted-foreground">{pri}</div>
                      <div className="text-xl font-bold">{s.pct}%</div>
                      <div className="text-[10px] text-muted-foreground">{s.breached}/{s.total} breached</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="fraud">
          <Card>
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle>Fraud alerts</CardTitle>
              <Button size="sm" onClick={scan} disabled={scanning} data-testid="fraud-scan-btn">
                {scanning ? "Scanning…" : "Re-scan all"}
              </Button>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-4 text-sm">
                <Badge variant="destructive">High {fraud.counts.high || 0}</Badge>
                <Badge className="bg-amber-500">Medium {fraud.counts.medium || 0}</Badge>
                <Badge variant="outline">Low {fraud.counts.low || 0}</Badge>
              </div>
              {fraud.rows.length === 0 ? (
                <div className="text-sm text-muted-foreground">No flagged claims. Run a scan to update.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead><tr className="text-xs text-muted-foreground text-left"><th className="p-2">Job</th><th>Customer</th><th>Severity</th><th>Hits</th></tr></thead>
                  <tbody>
                    {fraud.rows.map(r => (
                      <tr key={r.id} className="border-t border-border" data-testid={`fraud-row-${r.id}`}>
                        <td className="p-2 font-mono text-xs">{r.job_number || r.case_number}</td>
                        <td>{r.customer_name} <span className="text-xs text-muted-foreground">· {r.customer_email}</span></td>
                        <td><Badge variant={r.fraud_severity === "high" ? "destructive" : "outline"}>{r.fraud_severity}</Badge></td>
                        <td className="text-xs">{r.fraud_hits?.map(h => h.code).join(", ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="audit">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Scroll className="w-4 h-4" /> Audit log (recent 100)</CardTitle></CardHeader>
            <CardContent>
              <div className="max-h-[60vh] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-card"><tr className="text-xs text-muted-foreground text-left"><th className="p-2">At</th><th>Source</th><th>Kind</th><th>Actor</th><th>Entity</th></tr></thead>
                  <tbody>
                    {audit.map((r, i) => (
                      <tr key={i} className="border-t border-border/50">
                        <td className="p-2 text-xs">{r.at ? new Date(r.at).toLocaleString() : "—"}</td>
                        <td className="text-xs"><Badge variant="outline" className="text-[10px]">{r.source}</Badge></td>
                        <td className="text-xs">{r.kind || r.action}</td>
                        <td className="text-xs font-mono">{(r.actor_id || "—").slice(0, 8)}</td>
                        <td className="text-xs">{r.entity} {r.entity_id ? `#${r.entity_id.slice(0, 8)}` : ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
